<script setup>
// Warianty planu jako wejście (radio)
const selectedTier = ref("basic");
const price = computed(() => {
  return selectedTier.value === "basic"
    ? 500
    : selectedTier.value === "premium"
    ? 4500
    : 0;
});
defineExpose({
  selectedTier,
  price,
});

function updatePlan(tier) {
  selectedTier.value = tier;
}
</script>

<template>
  <article>
    <header class="mb-4">
      <h2 class="text-2xl font-semibold tracking-tight text-slate-900">
        Zanim zaczniemy projekt Twojej strony — wybierz, jak szczegółowo zaplanujemy jej
        sukces.
      </h2>
      <p class="mt-2 text-slate-600">
        Każda strona potrzebuje solidnych podstaw. Zdecyduj, czy ruszamy szybko, czy
        najpierw porządnie układamy strategię, by Twoja strona od razu działała na Twoją
        korzyść.
      </p>
    </header>

    <!-- Wprowadzenie -->
    <div class="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-slate-700">
      <p class="mt-1">Dobierz wariant dopasowany do Twoich oczekiwań i budżetu.</p>
    </div>

    <div role="radiogroup" aria-label="Wybór planu" class="grid gap-4 md:grid-cols-2">
      <!-- Plan podstawowy — szybki start -->
      <label
        class="group relative cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md"
        :class="selectedTier == 'basic' ? 'ring-2 ring-slate-900 bg-slate-50' : ''"
        @click="updatePlan('basic')"
      >
        <div class="flex h-full flex-col gap-3">
          <div class="flex items-center gap-3">
            <div
              class="grid w-12 aspect-square place-items-center rounded-xl bg-gradient-to-br from-slate-900 to-slate-700 text-white shadow"
            >
              💠
            </div>
            <h3 class="text-lg font-semibold text-slate-900">
              Plan podstawowy — szybki start
            </h3>
          </div>

          <p id="basic-desc" class="text-slate-700">
            Zbieramy i analizujemy pod kątem pozycjonowania najważniejsze informacje o
            Twojej firmie, klientach i celach, by zaprojektować stronę spójną z Twoim
            biznesem i gotową do działania.
          </p>

          <!-- BADGES (przywrócone) -->
          <div class="mt-1 flex flex-wrap gap-2">
            <span
              class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700"
            >
              Profil firmy
            </span>
            <span
              class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700"
            >
              Kim są klienci
            </span>
            <span
              class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700"
            >
              Cel strony
            </span>
          </div>

          <div class="mt-auto flex items-baseline gap-2">
            <span class="text-3xl font-bold text-slate-900">500</span>
            <span class="text-sm text-slate-500">zł</span>
          </div>
        </div>
      </label>

      <!-- Plan zaawansowany — maksymalne efekty (premium) -->
      <label
        class="group relative cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md"
        :class="selectedTier == 'premium' ? 'ring-2 ring-slate-900 bg-slate-50' : ''"
        @click="updatePlan('premium')"
      >
        <div class="flex h-full flex-col gap-3">
          <div class="flex items-center gap-3">
            <div
              class="grid w-12 aspect-square place-items-center rounded-xl bg-gradient-to-br from-indigo-600 to-sky-500 text-white shadow"
            >
              ✨
            </div>
            <h3 class="text-lg font-semibold text-slate-900">
              Plan zaawansowany — maksymalne efekty
            </h3>
          </div>

          <p id="premium-desc" class="text-slate-700">
            Pełna strategia w pigułce: poznajemy klientów, przewagi i słowa kluczowe, a
            potem układamy treści oraz ścieżki tak, by strona realnie dowoziła wyniki.
          </p>

          <!-- BADGES (przywrócone) -->
          <div class="mt-1 flex flex-wrap gap-2">
            <span
              class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700"
              title="Dawniej: STP — segmentacja, targetowanie, pozycjonowanie"
            >
              Segmenty klientów i pozycja marki
            </span>
            <span
              class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700"
              title="Dawniej: JTBD — zadania, motywacje, obawy"
            >
              Motywacje i zadania klientów
            </span>
            <span
              class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700"
              title="Analiza porównawcza rynku"
            >
              Porównanie z konkurencją
            </span>
            <span
              class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700"
              title="Dawniej: SWOT"
            >
              Mocne strony i ryzyka
            </span>
            <span
              class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700"
              title="Propozycje fraz do pozycjonowania"
            >
              Słowa kluczowe SEO
            </span>
            <span
              class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700"
              title="Struktura i plan treści pod SEO i konwersję"
            >
              Mapy treści i architektura
            </span>
            <span
              class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700"
              title="Lejek sprzedażowy i mierniki sukcesu"
            >
              Ścieżki użytkownika i cele
            </span>
          </div>

          <div class="mt-auto flex items-baseline gap-2">
            <span class="text-3xl font-bold text-slate-900">4500</span>
            <span class="text-sm text-slate-500">zł</span>
          </div>
        </div>
      </label>
    </div>
  </article>
</template>
